import type { AxiosError } from 'axios';

import type {
  CalculationTactic,
  DomainGuid,
  FailedCalculationTactic,
  CompletedCalculationTactic,
  EnrichedCompletedCalculationTactic
} from '@/features/execute-calculation-set/overmind/calculation';
import { UnexpectedStateError } from '@/overmind/errors';

import { ConfigBuilder } from './ConfigBuilder';
import type { Api, ApiConfig } from './apiFactory';
import apiFactory from './apiFactory';

export type TacticId = string;
export type CalculationSets = Record<
  TacticId,
  { name: string; type: 'LowVoltage' | 'MediumVoltage'; sequenceNumber: number }
>;
let api: Api;
type CalculationSetType = 'LowVoltage' | 'MediumVoltage';
export type CalculationResponse = {
  calculationSetId: string;
  calculationSetName: string;
  type: CalculationSetType;
  actions: [
    {
      name: string;
      href: string;
      type: CalculationSetActionType;
    }
  ];
};
type CalculationSetActionType = 'ExecuteLowVoltageShortCircuit';
const noBody = {};
export class EndpointUrl {
  private _uri: string;
  private _queryParameters: string[] = [];
  private constructor(uri: string) {
    this._uri = uri;
  }

  static from(uri: string) {
    return new EndpointUrl(uri);
  }

  withQueryParameter(queryParameterName: string, queryParameterValue: string) {
    this._queryParameters.push(
      `${queryParameterName}=${encodeURIComponent(queryParameterValue)}`
    );
    return this;
  }

  toStringUrl() {
    if (this._queryParameters.length === 0) return this._uri;
    return `${this._uri}?${this._queryParameters.join('&')}`;
  }
}
export default {
  initialize(config: ApiConfig) {
    api = apiFactory(config, 'Bearer');
  },
  setToken(newToken: string) {
    api.setToken(newToken);
  },
  setBasePath(basePath: string) {
    api.setBasePath(basePath);
  },
  runCalculation(
    featureId: DomainGuid,
    tenantId: string,
    tacticId: string,
    arcgisToken?: string,
    gridVersionName?: string,
    controller?: AbortController
  ) {
    if (!tenantId || !tacticId) {
      throw new UnexpectedStateError();
    }
    const config = new ConfigBuilder()
      .withRequestAborting(controller)
      .withEsriAuthorizationHeader(arcgisToken)
      .build();
    const executeCalculationSetUri = EndpointUrl.from(
      `/v1/tenants/${tenantId}/calculation-sets/${tacticId}/by-related-object/${featureId}/execute`
    );
    try {
      return (
        !gridVersionName
          ? api.post<CalculationTactic>(
              executeCalculationSetUri.toStringUrl(),
              noBody,
              config
            )
          : api.post<CalculationTactic>(
              executeCalculationSetUri
                .withQueryParameter('gridVersionName', gridVersionName)
                .toStringUrl(),
              noBody,
              config
            )
      ).then(result => {
        const completedCalculationTactic = result as CompletedCalculationTactic;

        const enrichedCompletedCalculationTactic =
          FutureExpectedResponseEnricher.fromTactic(completedCalculationTactic)
            .addVoltageToExternalNetworkInjectionsInFunctionalGrid()
            .eachLoadFlowScenarioHasItsOwnStartValues()
            .eachShortCircuitScenarioHasItsOwnStartValues()
            .build();

        return enrichedCompletedCalculationTactic;
      });
    } catch (error) {
      const axiosError = error as AxiosError<FailedCalculationTactic>;
      const message = axiosError.response?.data.details || axiosError.message;
      throw new Error(message);
    }
  },
  getAllCalculationSets(
    tenantId: string,
    controller?: AbortController
  ): Promise<CalculationSets> {
    if (!tenantId) {
      throw new UnexpectedStateError();
    }
    return api
      .get<CalculationResponse[]>(
        `v1/${tenantId}/calculation-sets/configurations/`,
        undefined,
        controller ? { signal: controller.signal } : undefined
      )
      .then(res => {
        const newCalculationSets: CalculationSets = toCalculationSets(res);
        return newCalculationSets;
      });
  },
  getCalculationSetsForRelatedObject(
    tenantId: string,
    relatedObjectId: DomainGuid,
    arcgisToken?: string,
    gridVersionName?: string,
    controller?: AbortController
  ): Promise<CalculationSets> {
    if (!tenantId || !relatedObjectId) {
      throw new UnexpectedStateError();
    }
    const config = new ConfigBuilder()
      .withRequestAborting(controller)
      .withEsriAuthorizationHeader(arcgisToken)
      .build();
    const getCalculationSetsUri = EndpointUrl.from(
      `v1/tenants/${tenantId}/calculation-sets/related-object/${relatedObjectId}`
    );
    if (gridVersionName !== undefined) {
      getCalculationSetsUri.withQueryParameter(
        'gridVersionName',
        gridVersionName
      );
    }
    return api
      .get<CalculationResponse[]>(
        getCalculationSetsUri.toStringUrl(),
        undefined,
        config
      )
      .then(res => {
        const newCalculationSets: CalculationSets = toCalculationSets(res);
        return newCalculationSets;
      })
      .catch(_ => {
        return this.getAllCalculationSets(tenantId, controller);
      });
  }
};

function enrichFunctionalGrid(
  completedCalculationTactic: CompletedCalculationTactic
): EnrichedCompletedCalculationTactic {
  const completedCalculationTacticCopy: EnrichedCompletedCalculationTactic =
    JSON.parse(JSON.stringify(completedCalculationTactic));

  const externalNetworkInjections =
    completedCalculationTacticCopy.calculationSetResult.grid.functional.externalNetworkInjections.map(
      extNetInj => extNetInj
    );

  // TODO: Voltage should be returned from the API
  externalNetworkInjections.forEach(function (extNetInj) {
    extNetInj.voltage = extNetInj.nominalVoltage * extNetInj.voltageFactor;
  });

  return completedCalculationTacticCopy;
}

export function toCalculationSets(res: CalculationResponse[]) {
  const newCalculationSets: CalculationSets = {};
  res.forEach(
    calcSet =>
      (newCalculationSets[calcSet.calculationSetId] = {
        name: calcSet.calculationSetName,
        type: calcSet.type,
        sequenceNumber: getSequenceNumber(calcSet)
      })
  );
  return newCalculationSets;
}
class FutureExpectedResponseEnricher {
  private enrichedCompletedCalculationTactic: EnrichedCompletedCalculationTactic;
  constructor(calculationTactic: CompletedCalculationTactic) {
    this.enrichedCompletedCalculationTactic = JSON.parse(
      JSON.stringify(calculationTactic)
    );
  }

  static fromTactic(calculationTactic: CompletedCalculationTactic) {
    return new FutureExpectedResponseEnricher(calculationTactic);
  }

  addVoltageToExternalNetworkInjectionsInFunctionalGrid() {
    this.enrichedCompletedCalculationTactic = enrichFunctionalGrid(
      this.enrichedCompletedCalculationTactic
    );
    return this;
  }

  eachLoadFlowScenarioHasItsOwnStartValues() {
    this.enrichedCompletedCalculationTactic.calculationSetResult.calculationScenarios.loadFlow.forEach(
      scenario => {
        scenario.inputs.startValues =
          this.enrichedCompletedCalculationTactic.calculationSetResult.grid.functional.externalNetworkInjections.map(
            externalNetworkInjection => ({
              voltage: externalNetworkInjection.voltage,
              id: externalNetworkInjection.externalNetInjId,
              connectedToId: externalNetworkInjection.objectId
            })
          );
      }
    );

    return this;
  }

  eachShortCircuitScenarioHasItsOwnStartValues() {
    this.enrichedCompletedCalculationTactic.calculationSetResult.calculationScenarios.shortCircuit.forEach(
      scenario => {
        const startValues =
          this.enrichedCompletedCalculationTactic.calculationSetResult.grid.functional.externalNetworkInjections.map(
            externalNetworkInjection => ({
              connectedToId: externalNetworkInjection.objectId,
              minInitialSymShCCurrent:
                externalNetworkInjection.minInitialSymShCCurrent,
              maxInitialSymShCCurrent:
                externalNetworkInjection.maxInitialSymShCCurrent,
              id: externalNetworkInjection.externalNetInjId,
              powerFactorMin: externalNetworkInjection.cosPhiMin,
              powerFactorMax: externalNetworkInjection.cosPhiMax
            })
          );
        scenario.inputs = {
          startValues
        };
      }
    );

    return this;
  }

  build() {
    return this.enrichedCompletedCalculationTactic;
  }
}
function getSequenceNumber(calcSet: CalculationResponse): number {
  if (calcSet.calculationSetName.includes('Low Voltage Three Load Scenario')) {
    return 0;
  }
  if (calcSet.calculationSetName.includes('Low Voltage Capacity')) {
    return 1;
  }
  if (calcSet.calculationSetName.includes('Low Voltage Short Circuit')) {
    return 2;
  }
  if (calcSet.calculationSetName.includes('Medium Voltage Capacity')) {
    return 3;
  }
  if (calcSet.calculationSetName.includes('Medium Voltage Short Circuit')) {
    return 4;
  }

  return 5;
}
